import { nodeAABB, nodeBox } from './bounds.js';
import { connectorGeometry, type NodeLookup } from './connector.js';
import {
  distToPolyline,
  pointInPolygon,
  polylineIntersectsRect,
  rectCenter,
  rectContainsPoint,
  rectContainsRect,
  rectExpand,
  rectIntersects,
  vRotate,
} from './geometry.js';
import { flattenPath } from './path.js';
import { shapePath } from './shapes.js';
import type { BoardNode, DrawNode, NodeId, Rect, Vec } from './types.js';

export interface HitOptions {
  lookup: NodeLookup;
  /** Extra pick radius in board units; scale this with zoom at the call site. */
  tolerance?: number;
  /** Ignore nodes the user cannot touch. */
  includeLocked?: boolean;
  /** Frames are usually only grabbable by their title bar and border. */
  frameEdgeOnly?: boolean;
  /**
   * Height of the frame title strip in board units. The label is painted in
   * screen pixels, so callers that know the zoom pass `FRAME_HEADER_HEIGHT /
   * zoom` to keep the hit area the same size as the text it stands for.
   */
  frameHeaderHeight?: number;
}

/** Height of the clickable title strip above a frame, in board units. */
export const FRAME_HEADER_HEIGHT = 24;

function toLocal(node: BoardNode, p: Vec): Vec {
  if (node.rotation === 0) return p;
  return vRotate(p, rectCenter(nodeBox(node)), -node.rotation);
}

function drawPolyline(node: DrawNode): Vec[] {
  // Points are stored in the box local frame and scale with w/h, so a stroke
  // survives being resized as part of a multi-select.
  const bounds = boundsOfDrawPoints(node);
  const sx = bounds.w === 0 ? 1 : node.w / bounds.w;
  const sy = bounds.h === 0 ? 1 : node.h / bounds.h;
  return node.points.map((p) => ({
    x: node.x + (p[0] - bounds.x) * sx,
    y: node.y + (p[1] - bounds.y) * sy,
  }));
}

/** Bounds of the raw stored points, needed to normalise draw geometry. */
export function boundsOfDrawPoints(node: DrawNode): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of node.points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** World-space polyline for a stroke, shared by the renderer and the hit-tester. */
export function drawWorldPoints(node: DrawNode): Vec[] {
  return drawPolyline(node);
}

export function hitTestNode(node: BoardNode, point: Vec, options: HitOptions): boolean {
  const tol = options.tolerance ?? 4;
  if (node.hidden) return false;
  if (node.locked && !options.includeLocked) return false;

  switch (node.type) {
    case 'connector': {
      const geo = connectorGeometry(node, options.lookup);
      return distToPolyline(geo.points, point) <= Math.max(node.strokeWidth, 1) / 2 + tol;
    }

    case 'draw': {
      const reach = node.size / 2 + tol;
      // Hover tests every stroke on the board on each pointer move; the
      // box check keeps that from touching each stroke's points.
      if (!rectContainsPoint(rectExpand(nodeAABB(node, options.lookup), reach), point)) {
        return false;
      }
      const pts = drawPolyline(node);
      const local = toLocal(node, point);
      if (node.closed && pointInPolygon(pts, local)) return true;
      return distToPolyline(pts, local) <= reach;
    }

    case 'shape': {
      const local = toLocal(node, point);
      const polys = flattenPath(shapePath(node.shape, nodeBox(node), node.style.radius), 12);
      const hollow = node.style.fillStyle === 'none' && node.text.length === 0;
      for (const poly of polys) {
        if (!hollow && pointInPolygon(poly, local)) return true;
        if (distToPolyline(poly, local) <= node.style.strokeWidth / 2 + tol) return true;
      }
      return false;
    }

    case 'frame': {
      const box = nodeBox(node);
      const headerH = options.frameHeaderHeight ?? FRAME_HEADER_HEIGHT;
      const header: Rect = {
        x: box.x,
        y: box.y - headerH,
        w: box.w,
        h: headerH,
      };
      if (rectContainsPoint(header, point)) return true;
      if (!options.frameEdgeOnly) return rectContainsPoint(box, point);
      // Edge-only: a band along the border.
      const inner: Rect = {
        x: box.x + tol * 2,
        y: box.y + tol * 2,
        w: Math.max(box.w - tol * 4, 0),
        h: Math.max(box.h - tol * 4, 0),
      };
      return rectContainsPoint(box, point) && !rectContainsPoint(inner, point);
    }

    case 'comment': {
      // Comment pins are a fixed-size dot anchored at the node origin.
      const r = Math.max(node.w, 16) / 2 + tol;
      const c = { x: node.x + node.w / 2, y: node.y + node.h / 2 };
      return (point.x - c.x) ** 2 + (point.y - c.y) ** 2 <= r * r;
    }

    default: {
      const local = toLocal(node, point);
      return rectContainsPoint(nodeBox(node), local);
    }
  }
}

/**
 * Topmost node under the pointer. `nodes` must be in paint order (bottom
 * first); the scan runs backwards so the visually top item wins.
 */
export function hitTestPoint(
  nodes: readonly BoardNode[],
  point: Vec,
  options: HitOptions,
): BoardNode | null {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]!;
    if (hitTestNode(node, point, options)) return node;
  }
  return null;
}

export type MarqueeMode = 'intersect' | 'contain';

export function hitTestRect(
  nodes: readonly BoardNode[],
  area: Rect,
  options: HitOptions & { mode?: MarqueeMode },
): BoardNode[] {
  const mode = options.mode ?? 'intersect';
  const out: BoardNode[] = [];

  for (const node of nodes) {
    if (node.hidden) continue;
    if (node.locked && !options.includeLocked) continue;
    const aabb = nodeAABB(node, options.lookup);

    if (mode === 'contain') {
      if (rectContainsRect(area, aabb)) out.push(node);
      continue;
    }

    if (!rectIntersects(area, aabb)) continue;

    // Cheap AABB pass is enough for boxes; thin geometry needs the real shape
    // so a marquee near a diagonal line does not grab it by its bounding box.
    if (node.type === 'connector') {
      const geo = connectorGeometry(node, options.lookup);
      if (polylineIntersectsRect(geo.points, area)) out.push(node);
      continue;
    }
    if (node.type === 'draw') {
      if (polylineIntersectsRect(drawPolyline(node), area)) out.push(node);
      continue;
    }
    out.push(node);
  }
  return out;
}

/**
 * Walks up the parent chain to the outermost group, so clicking a grouped item
 * selects the group. Frames are containers but not selection units, so the walk
 * stops at them.
 */
export function resolveSelectionTarget(
  node: BoardNode,
  lookup: NodeLookup,
  /** True while the user is inside a group after a double-click. */
  enteredGroups: ReadonlySet<NodeId> = new Set(),
): BoardNode {
  let current = node;
  for (let depth = 0; depth < 32; depth++) {
    const parentId = current.parentId;
    if (!parentId) return current;
    const parent = lookup(parentId);
    if (!parent || parent.type !== 'group') return current;
    if (enteredGroups.has(parent.id)) return current;
    current = parent;
  }
  return current;
}
